/**
 * AWAC i18n — bilingual strings (EN default) for the Le Lib Mont-Saint-Hilaire 55+ pilot.
 *
 * English-default per pilot preference (NOTE: for a public Quebec launch,
 * Bill 96 requires French-first presentation — flip the default back to 'fr').
 * Resolution: a ?lang=en|fr URL parameter (before or inside the hash, e.g.
 * site/?lang=fr or site/#/units?lang=fr) is applied ONCE at load and seeds
 * the stored preference (localStorage 'awac_lang'); afterwards the stored
 * preference rules, so the EN/FR toggle keeps working while the param sits
 * in the address bar. No param, no store -> default 'en'.
 *
 * Components subscribe through useAwacLang(); calling setLang() persists the
 * preference and re-renders every subscriber via the 'awac:lang-changed' event.
 */

import { useEffect, useState } from 'react';

const LANG_KEY = 'awac_lang';
const SUPPORTED = ['fr', 'en'];

const STRINGS = {
    fr: {
        // ----- Landing: nav -----
        'nav.pilot': 'Pilote',
        'nav.residences': 'Résidences',
        'nav.neighborhood': 'Quartier',
        'nav.neighborhoodTab': 'Le quartier',
        'nav.unitsTab': 'Les unités',
        'nav.backHome': 'Accueil',
        'nav.chat': 'Clavarder avec Lia',
        'nav.chatShort': 'Clavarder',
        // Short labels for the phone page tabs — the full names wrap at 393px.
        'nav.tabHome': 'Accueil',
        'nav.tabUnits': 'Unités',
        'nav.tabMap': 'Carte',
        'nav.menu': 'Menu',
        // Persistent phone Lia bar — context-aware placeholder per page.
        'bar.hintHome': 'Demandez à Lia : plans, prix…',
        'bar.hintUnits': 'Demandez à Lia à propos de ces unités…',
        // ----- Units page (unitsPage.* — 'units.*' belongs to the UnitCarousel template) -----
        'unitsPage.title': 'Toutes nos unités',
        'unitsPage.lede': '{count} résidences locatives réelles du projet — filtrez par chambres et demandez à Lia les détails de n’importe quelle unité.',
        'unitsPage.filterLabel': 'Filtrer par chambres',
        'unitsPage.filter.all': 'Toutes',
        'unitsPage.filter.oneBed': '1 chambre',
        'unitsPage.filter.twoBed': '2 chambres',
        'unitsPage.filter.threeBed': '3 chambres',
        'unitsPage.filter.den': 'Avec den',
        'unitsPage.sortLabel': 'Trier :',
        'unitsPage.sort.unit': 'Numéro d’unité',
        'unitsPage.sort.priceAsc': 'Prix (croissant)',
        'unitsPage.sort.priceDesc': 'Prix (décroissant)',
        'unitsPage.count': '{shown} unités affichées sur {total}',
        'unit.name': 'Unité {unit}',
        'unit.den': 'den',
        'unit.corner': 'unité de coin',
        'unit.interior': 'int.',
        'unit.exterior': 'ext.',
        'unit.floor': 'étage {floor}',
        'unit.perMonth': '/mois',
        'unit.ask': 'Demander à Lia',
        'unit.askMessage': 'Parlez-moi de l’unité {unit}',
        // ----- Landing: hero -----
        'hero.eyebrow': 'Complexe locatif 55+ · Mont-Saint-Hilaire',
        'hero.titlePre': 'Bien plus qu’un ',
        'hero.titleEm': 'condo locatif',
        'hero.lede':
            'Le Lib Mont-Saint-Hilaire, c’est la liberté de vivre au pied de la montagne : 132 appartements locatifs pour les 55 ans et plus, signés EMD-Batimo, entre le Richelieu et la réserve naturelle Gault. Câble, internet, électricité et climatisation inclus — livraison juin 2026.',
        'hero.chat': 'Clavarder avec Lia',
        'hero.viewResidences': 'Voir les résidences',
        'hero.fact1': 'Complexe locatif 55+',
        'hero.fact2': 'Livraison juin 2026',
        'hero.fact3': 'Câble, internet et électricité inclus',
        // ----- Landing: residences -----
        'residences.title': 'Les résidences',
        'residences.lede':
            'Du 3½ au 5½ — comptoirs de quartz, grande fenestration, balcon et climatisation. Chaque rangée montre l\u2019unité la plus abordable de sa configuration, tirée de l\u2019inventaire réel. Demandez à Lia les détails, les photos et les disponibilités.',
        'plan.bed': 'ch.',
        'plan.bath': 'sdb',
        'plan.layout': 'configuration',
        'plan.sqft': 'pi\u00B2',
        'plan.interior': 'superficie intérieure',
        'plan.asking': 'prix demandé',
        'plan.from': 'à partir de',
        'plan.ask': 'Demander à Lia →',
        'plan.askMessage': 'Parle-moi du {plan}',
        'plans.note': 'Les prix et disponibilités affichés sont des données pilotes et peuvent changer.',
        // ----- Landing: neighborhood -----
        'neighborhood.title': 'Le quartier',
        'neighborhood.p1':
            'Mont-Saint-Hilaire vit entre sa montagne et le Richelieu — un cœur de village où marché, boulangerie primée, pharmacies, CLSC et gare exo sont à quelques minutes du Lib. Aux portes du complexe : les sentiers de la réserve naturelle Gault, les vergers et le Vieux-Beloeil juste en face.',
        'neighborhood.p2':
            'Lia peut cartographier ce qui compte pour vous — épiceries, santé, restos, culture et sentiers autour du Lib — directement dans la conversation.',
        'askList.title': 'Des questions pour Lia',
        'askList.1': 'Montre-moi les plans',
        'askList.2': 'Montre-moi les 4½ disponibles autour de 2 500 $ par mois',
        'askList.3': 'Montre-moi le quartier',
        'askList.4': 'Réserver une visite avec l\u2019équipe des ventes',
        // ----- Landing: CTA -----
        'cta.title': 'Lia est sur la page.',
        'cta.lede':
            'Notre agente locative IA répond en quelques secondes — plans, unités, carte du quartier et réservation de visites, en français ou en anglais.',
        'cta.chip1': 'Montre-moi les plans',
        'cta.chip2': 'Montre-moi le quartier',
        'cta.chip3': 'Qu\u2019est-ce que je peux avoir pour 2 500 $ par mois?',
        'cta.start': 'Commencer la conversation',
        // ----- Landing: footer -----
        'footer.blurb':
            'Une expérience pilote par AI Développement Immobilier Inc. Les conversations sur cette page sont prises en charge par Lia, une agente IA construite sur Salesforce Agentforce.',
        'footer.fine':
            'Ceci est un environnement de test — les inscriptions, prix et disponibilités sont des données d\u2019exemple pour le pilote.',
        // ----- Proactive nudge -----
        'nudge.text': 'Des questions sur les plans ou les prix? Lia répond en quelques secondes.',
        'nudge.cta': 'Poser une question',
        'nudge.dismiss': 'Ignorer',
        'nudge.message': 'J\u2019ai des questions sur le Lib Mont-Saint-Hilaire.',
        // ----- Toasts (agent-driven navigation & map results) -----
        'toast.mapNav': '\ud83d\udccd Lia vous am\u00e8ne au quartier',
        'toast.unitsNav': '\ud83c\udfe0 Lia vous montre les unit\u00e9s',
        'toast.mapFound': '\ud83d\udccd {name} \u00b7 {dist}',
        'toast.mapCount': '\ud83d\udccd {count} r\u00e9sultats affich\u00e9s sur la carte',
        'toast.mapNone': 'Aucun r\u00e9sultat trouv\u00e9 \u00e0 proximit\u00e9 \u2014 demandez-moi autre chose!',
        // ----- Neighborhood page ask-chips -----
        'mapChips.label': 'Demandez \u00e0 Lia :',
        'mapChips.1': 'O\u00f9 est l\u2019\u00e9picerie la plus proche?',
        'mapChips.2': 'Montre-moi les \u00e9coles autour du projet',
        'mapChips.3': 'O\u00f9 est le parc canin?',
        // ----- Chat chrome -----
        'chat.subtitle': 'Agente immobilière IA',
        'chat.inputPlaceholder': 'Écrivez votre message…',
        'chat.bookedChip': 'Visite réservée',
        'chat.minimize': 'Réduire la fenêtre de clavardage',
        'chat.close': 'Fermer la conversation',
        'chat.closeWindow': 'Fermer la fenêtre',
        'chat.transcriptLabel': 'Messages de la conversation',
        'chat.scrollLatest': 'Aller au dernier message',
        'chat.suggestionsLabel': 'Demandez-moi par exemple :',
        'chat.suggestion1': 'Réserver une visite',
        'chat.suggestion2': 'Montre-moi le quartier',
        'chat.suggestionsDismiss': 'Masquer les suggestions',
        'chat.send': 'Envoyer le message',
        'chat.terms': 'En continuant, vous acceptez nos conditions. Cette conversation peut être enregistrée.',
        'chat.endTitle': 'Terminer la conversation?',
        'chat.endBody':
            'Votre conversation avec Lia sera fermée. Vous pourrez en démarrer une nouvelle à tout moment.',
        'chat.endConfirm': 'Terminer',
        'chat.endCancel': 'Annuler',
        'chat.brokenEnvelope': 'Hmm, la mise à jour visuelle n’a pas fonctionné cette fois-ci. Reformulez votre demande et je réessaie tout de suite.',
        'chat.suggestionUnits': 'Montre-moi les unités de 2 chambres',
        'chat.agentThinking': 'Lia prépare une réponse…',
        'chat.isTyping': '{name} écrit…',
        'chat.multipleTyping': 'Plusieurs personnes écrivent…',
        'unitsSummary.more': '+ {count} autres dans la galerie',
        // ----- Units page pounce (proactive engagement) -----
        'pounce.unit': 'L’unité {unit} vous intéresse? Demandez-moi ce que vous voulez — photos, disponibilité ou visite.',
        'pounce.unitCta': 'Parlez-moi de l’unité {unit}',
        'pounce.filtered': 'Vous comparez {count} unités? Dites-moi votre budget et je raffine la sélection.',
        'pounce.filteredCta': 'Aidez-moi à choisir',
        'pounce.dwell': 'Vous cherchez la bonne unité? Lia peut la trouver en quelques secondes.',
        'pounce.dwellCta': 'Demander à Lia',
        'pounce.filteredMessage': 'Aide-moi à choisir parmi les unités de {beds} chambres — quelles questions devrais-je me poser?',
        'pounce.dwellMessage': 'Aide-moi à trouver la bonne unité pour moi.',
        // ----- Template error boundary -----
        'tpl.errorTitle': 'Ce contenu n\u2019a pas pu être affiché.',
        'tpl.errorBody': 'Vous pouvez continuer la conversation — Lia a toujours toutes les informations.',
        'tpl.retry': 'Réessayer',
        // ----- Chat transcript chrome -----
        'chat.started': 'Conversation commencée le {date} à {time}',
        'chat.ended': 'Conversation terminée le {date} à {time}',
        'chat.joined': '{name} a rejoint la conversation · {time}',
        'chat.left': '{name} a quitté la conversation · {time}',
        'chat.you': 'Vous',
        'chat.senderAt': '{name} à {time}',
        'chat.readAt': 'Lu à {time} • ',
        'chat.deliveredAt': 'Livré à {time} • ',
        'chat.sent': 'Envoyé • ',
        // ----- Gallery panel -----
        'gallery.close': 'Fermer la galerie',
        'gallery.details': 'Détails',
        'gallery.unknownTitle': 'Ce contenu ne peut pas être affiché ici.',
        'gallery.unknownSub': 'Composant non pris en charge',
        'gallery.FloorPlanShowcase': 'Plans d\u2019étage',
        'gallery.UnitCarousel': 'Unités correspondantes',
        'gallery.NeighborhoodMap': 'Quartier',
        'gallery.BookingFrame': 'Réserver une visite',
        'gallery.BookingSlots': 'Réserver une visite',
        'gallery.BookingConfirm': 'Visite confirmée',
        'link.UnitCarousel': 'Voir les unités correspondantes',
        'link.NeighborhoodMap': 'Voir le quartier',
        'link.FloorPlanShowcase': 'Voir les plans',
        'link.PoiMapControl': 'Voir sur la carte',
        'link.gallery': 'Voir la galerie',
        // ----- Full-screen POI map (The Neighborhood page) -----
        'poiMap.updated': 'Carte du quartier mise à jour',
        'poiMap.noCommand': 'Aucune commande de carte reçue',
        'poiMap.iframeTitle': 'Carte interactive du quartier',
        // ----- Neighborhood map template -----
        'map.title': 'À proximité',
        'map.subtitle': 'Points d\u2019intérêt autour de {name}',
        'map.cat.Groceries': 'Épiceries',
        'map.cat.Pharmacies': 'Pharmacies',
        'map.cat.Restaurants': 'Restaurants',
        'map.cat.Parks': 'Parcs',
        'map.cat.Schools': 'Écoles',
        'map.cat.Fitness': 'Gyms et entraînement',
        'map.cat.Transit': 'Transport en commun',
        'map.cat.Shopping': 'Magasinage',
        'map.cat.Other': 'Autre',
        'map.loading': 'Chargement de la carte…',
        'map.error': 'La carte n\u2019a pas pu se charger. Vérifiez votre connexion et réessayez.',
        'map.footnote': 'Données cartographiques © contributeurs OpenStreetMap. L\u2019emplacement des épingles est illustratif.',
        'map.projectLocation': 'Emplacement du projet',
        'map.openInMaps': 'Ouvrir dans Cartes ›',
        'map.filterLabel': 'Filtrer les points d\u2019intérêt par catégorie',
        'map.mapLabel': 'Carte du quartier',
        // ----- Unit carousel / floor plans templates -----
        'units.title': 'Unités correspondantes',
        'units.noPhotos': 'Aucune photo pour le moment',
        'units.empty': 'Aucune unité correspondante à afficher.',
        'units.book': 'Réserver une visite',
        'units.bed': 'ch.',
        'units.beds': 'ch.',
        'units.bath': 'sdb',
        'units.baths': 'sdb',
        'units.sqft': 'pi\u00B2',
        'units.sqftBalcony': 'pi\u00B2 balcon',
        'units.den': '+ den',
        'units.interior': 'int\u00E9rieur',
        'units.floorN': '\u00C9tage {n}',
        'units.maximize': 'Agrandir les photos',
        'units.minimize': 'Retour \u00E0 toutes les unit\u00E9s',
        'units.prevUnit': 'R\u00E9sidence pr\u00E9c\u00E9dente',
        'units.nextUnit': 'R\u00E9sidence suivante',
        'units.nextHome': 'Suivante \u2192',
        'units.viewPlan': 'Voir le plan',
        'units.hidePlan': 'Masquer le plan',
        'units.nextUnitChip': 'R\u00E9sidence suivante \u2192',
        'units.pagerOf': 'R\u00E9sidence {n} de {count}',
        'units.goToUnit': 'Voir {name}',
        'units.quietFloor': '\u00C9tage {n} \u2014 plus haut, plus paisible',
        'note.stretch': 'Environ {v}% au-dessus du budget \u2014 retenue pour la correspondance parfaite',
        'note.underMin': '{d}/mois sous votre budget \u2014 correspond \u00e0 tout le reste',
        'note.bedsMore': 'Une chambre de plus que demand\u00E9, dans le budget',
        'note.denSub': 'Le den peut servir de bureau \u00E0 domicile',
        'note.bedOffice': 'Une des chambres peut servir de bureau \u00E0 domicile',
        'chat.fullscreen': 'Plein \u00E9cran',
        'chat.exitFullscreen': 'Quitter le plein \u00E9cran',
        'units.orientation': 'orientation',
        'units.orientationBullet': 'Orientation : {v}',
        'units.noiseBullet': 'Bruit routier : {v}',
        'units.viewBullet': 'Vue : {v}',
        'units.sunBullet': 'Ensoleillement : {v}',
        'units.privacyBullet': 'Intimité visuelle : {v}',
        'units.bed1Bullet': 'Chambre principale : {v}',
        'units.livingBullet': 'Salon : {v}',
        'units.kitchenBullet': 'Cuisine : {v}',
        'units.balconyDims': 'Balcon : {v}',
        'feat.island': '\u00CElot de cuisine',
        'feat.counterSpace': 'Grand espace de comptoir',
        'feat.counters': 'Comptoirs agrandis',
        'feat.corner': 'Unit\u00E9 de coin',
        'feat.den': 'Den',
        'feat.ensuite': 'Salle de bain attenante',
        'feat.walkIn': 'Walk-in \u00E0 l\u2019entr\u00E9e',
        'feat.washerDryer': 'Laveuse-s\u00E9cheuse c\u00F4te \u00E0 c\u00F4te',
        'feat.backyard': 'Cour arri\u00E8re',
        'feat.sideYard': 'Cour lat\u00E9rale',
        'feat.sun': 'Luminosit\u00E9 abondante',
        'match.full': 'Correspondance compl\u00E8te',
        'match.partial': 'Correspondance partielle',
        'miss.bed': '{v} chambre',
        'miss.beds': '{v} chambres',
        'miss.sqftMin': 'Au moins {v} pi\u00B2',
        'miss.sqftMax': 'Au plus {v} pi\u00B2',
        'miss.priceMax': 'Au plus {v}/mois',
        'miss.priceMin': 'Au moins {v}/mois',
        'miss.without': 'Sans : {v}',
        'units.photosOf': 'Photos de {name}',
        'units.photoAlt': '{name} — photo {n} de {count}',
        'units.prevPhoto': 'Photo précédente',
        'units.nextPhoto': 'Photo suivante',
        'fp.title': 'Plans d\u2019étage',
        'fp.empty': 'Aucun plan d\u2019étage à afficher.',
        'fp.imageFallback': 'Plan d\u2019étage',
        'fp.from': 'À partir de'
    },
    en: {
        'nav.pilot': 'Pilot',
        'nav.residences': 'Residences',
        'nav.neighborhood': 'Neighborhood',
        'nav.neighborhoodTab': 'The Neighborhood',
        'nav.unitsTab': 'The Units',
        'nav.backHome': 'Home',
        'nav.chat': 'Chat with Lia',
        'nav.chatShort': 'Chat',
        // Short labels for the phone page tabs — the full names wrap at 393px.
        'nav.tabHome': 'Home',
        'nav.tabUnits': 'Units',
        'nav.tabMap': 'Map',
        'nav.menu': 'Menu',
        // Persistent phone Lia bar — context-aware placeholder per page.
        'bar.hintHome': 'Ask Lia about plans or pricing…',
        'bar.hintUnits': 'Ask Lia about these units…',
        'unitsPage.title': 'All our units',
        'unitsPage.lede': '{count} real rental residences from the project — filter by bedrooms and ask Lia about any unit.',
        'unitsPage.filterLabel': 'Filter by bedrooms',
        'unitsPage.filter.all': 'All',
        'unitsPage.filter.oneBed': '1 bedroom',
        'unitsPage.filter.twoBed': '2 bedrooms',
        'unitsPage.filter.threeBed': '3 bedrooms',
        'unitsPage.filter.den': 'With den',
        'unitsPage.sortLabel': 'Sort:',
        'unitsPage.sort.unit': 'Unit number',
        'unitsPage.sort.priceAsc': 'Price (low to high)',
        'unitsPage.sort.priceDesc': 'Price (high to low)',
        'unitsPage.count': 'Showing {shown} of {total} units',
        'unit.name': 'Unit {unit}',
        'unit.den': 'den',
        'unit.corner': 'corner unit',
        'unit.interior': 'interior',
        'unit.exterior': 'exterior',
        'unit.floor': 'floor {floor}',
        'unit.perMonth': '/month',
        'unit.ask': 'Ask Lia',
        'unit.askMessage': 'Tell me about unit {unit}',
        'hero.eyebrow': '55+ Rental Complex · Mont-Saint-Hilaire',
        'hero.titlePre': 'So much more than a ',
        'hero.titleEm': 'rental condo',
        'hero.lede':
            'Le Lib Mont-Saint-Hilaire is the freedom of living at the foot of the mountain: 132 rental apartments for active adults 55 and over, by EMD-Batimo, between the Richelieu River and the Gault Nature Reserve. Cable, internet, electricity and A/C included — delivery June 2026.',
        'hero.chat': 'Chat with Lia',
        'hero.viewResidences': 'View the residences',
        'hero.fact1': '55+ rental complex',
        'hero.fact2': 'Delivery June 2026',
        'hero.fact3': 'Cable, internet & electricity included',
        'residences.title': 'The residences',
        'residences.lede':
            'From 3½ to 5½ — quartz counters, oversized windows, balcony and A/C. Each row shows the most affordable unit of its configuration, drawn from the real inventory. Ask Lia for details, photos and availability.',
        'plan.bed': 'bed',
        'plan.bath': 'bath',
        'plan.layout': 'layout',
        'plan.sqft': 'sq ft',
        'plan.interior': 'interior area',
        'plan.asking': 'asking price',
        'plan.from': 'starting at',
        'plan.ask': 'Ask Lia →',
        'plan.askMessage': 'Tell me about {plan}',
        'plans.note': 'Pricing and availability shown are pilot data and may change.',
        'neighborhood.title': 'The neighborhood',
        'neighborhood.p1':
            'Mont-Saint-Hilaire lives between its mountain and the Richelieu — a village heart where the market, an award-winning bakery, pharmacies, the CLSC and the exo train station are minutes from Le Lib. At the complex\u2019s doorstep: the Gault Nature Reserve trails, the orchards, and Old Beloeil just across the river.',
        'neighborhood.p2':
            'Lia can map what matters to you — groceries, healthcare, restaurants, culture and trails around Le Lib — right inside the conversation.',
        'askList.title': 'Things to ask Lia',
        'askList.1': 'Show me the plans',
        'askList.2': 'Show me available 4½ homes around $2,500 a month',
        'askList.3': 'Show me the neighborhood',
        'askList.4': 'Book a visit with the sales team',
        'cta.title': 'Lia is on the page.',
        'cta.lede':
            'Our AI leasing agent answers in seconds — floor plans, homes, the neighborhood map and visit bookings, in English or in French.',
        'cta.chip1': 'Show me the plans',
        'cta.chip2': 'Show me the neighborhood',
        'cta.chip3': 'What can I get for $2,500 a month?',
        'cta.start': 'Start the conversation',
        'footer.blurb':
            'A pilot experience by AI Développement Immobilier Inc. Conversations on this page are handled by Lia, an AI agent built on Salesforce Agentforce.',
        'footer.fine':
            'This is a test environment — listings, pricing and availability are sample data for the pilot.',
        'nudge.text': 'Questions about the plans or pricing? Lia answers in seconds.',
        'nudge.cta': 'Ask a question',
        'nudge.dismiss': 'Dismiss',
        'nudge.message': 'I have questions about Le Lib Mont-Saint-Hilaire.',
        'toast.mapNav': '📍 Lia is taking you to the neighborhood',
        'toast.unitsNav': '🏠 Lia is showing you the units',
        'toast.mapFound': '📍 {name} · {dist}',
        'toast.mapCount': '📍 {count} results shown on the map',
        'toast.mapNone': 'No results found nearby — ask me about something else!',
        'mapChips.label': 'Ask Lia:',
        'mapChips.1': 'Where is the nearest grocery store?',
        'mapChips.2': 'Show me the schools around the project',
        'mapChips.3': 'Where is the dog park?',
        'chat.subtitle': 'AI Real Estate Agent',
        'chat.inputPlaceholder': 'Type your message…',
        'chat.bookedChip': 'Visit booked',
        'chat.minimize': 'Minimize chat window',
        'chat.close': 'Close the conversation',
        'chat.closeWindow': 'Close window',
        'chat.transcriptLabel': 'Conversation messages',
        'chat.scrollLatest': 'Scroll to latest message',
        'chat.suggestionsLabel': 'Ask me things like:',
        'chat.suggestion1': 'Book a visit',
        'chat.suggestion2': 'Show me the neighborhood',
        'chat.suggestionsDismiss': 'Dismiss suggestions',
        'chat.send': 'Send message',
        'chat.terms': 'By continuing, you agree to our terms. This conversation may be recorded.',
        'chat.endTitle': 'End the conversation?',
        'chat.endBody': 'Your conversation with Lia will be closed. You can start a new one anytime.',
        'chat.endConfirm': 'End conversation',
        'chat.endCancel': 'Cancel',
        'chat.brokenEnvelope': 'Hmm — that visual update didn’t come through this time. Rephrase your request and I’ll try again right away.',
        'chat.suggestionUnits': 'Show me the 2-bedroom units',
        'chat.agentThinking': 'Lia is preparing a reply…',
        'chat.isTyping': '{name} is typing…',
        'chat.multipleTyping': 'Several people are typing…',
        'unitsSummary.more': '+ {count} more in the gallery',
        'pounce.unit': 'Unit {unit} catching your eye? Ask me anything — photos, availability, or a visit.',
        'pounce.unitCta': 'Tell me about unit {unit}',
        'pounce.filtered': 'Comparing {count} homes? Tell me your budget and I’ll narrow it down.',
        'pounce.filteredCta': 'Help me choose',
        'pounce.dwell': 'Looking for the right home? Lia can find it in seconds.',
        'pounce.dwellCta': 'Ask Lia',
        'pounce.filteredMessage': 'Help me choose between the {beds}-bedroom units — what should I be asking?',
        'pounce.dwellMessage': 'Help me find the right unit for me.',
        'tpl.errorTitle': 'This content could not be displayed.',
        'tpl.errorBody': 'You can keep chatting — Lia still has all the information.',
        'tpl.retry': 'Retry',
        'chat.started': 'Conversation started: {date} at {time}',
        'chat.ended': 'Conversation ended: {date} at {time}',
        'chat.joined': '{name} joined the conversation · {time}',
        'chat.left': '{name} left the conversation · {time}',
        'chat.you': 'You',
        'chat.senderAt': '{name} at {time}',
        'chat.readAt': 'Read at {time} • ',
        'chat.deliveredAt': 'Delivered at {time} • ',
        'chat.sent': 'Sent • ',
        'gallery.close': 'Close gallery',
        'gallery.details': 'Details',
        'gallery.unknownTitle': 'This content can\u2019t be displayed here.',
        'gallery.unknownSub': 'Unsupported component',
        'gallery.FloorPlanShowcase': 'Floor Plans',
        'gallery.UnitCarousel': 'Matching units',
        'gallery.NeighborhoodMap': 'Neighborhood',
        'gallery.BookingFrame': 'Book a visit',
        'gallery.BookingSlots': 'Book a visit',
        'gallery.BookingConfirm': 'Visit confirmed',
        'link.UnitCarousel': 'View matching units',
        'link.NeighborhoodMap': 'View neighborhood',
        'link.FloorPlanShowcase': 'View floor plans',
        'link.PoiMapControl': 'View on the map',
        'link.gallery': 'View gallery',
        'poiMap.updated': 'Neighborhood map updated',
        'poiMap.noCommand': 'No map command received',
        'poiMap.iframeTitle': 'Interactive neighborhood map',
        'map.title': 'What\u2019s nearby',
        'map.subtitle': 'Points of interest around {name}',
        'map.cat.Groceries': 'Groceries',
        'map.cat.Pharmacies': 'Pharmacies',
        'map.cat.Restaurants': 'Restaurants',
        'map.cat.Parks': 'Parks',
        'map.cat.Schools': 'Schools',
        'map.cat.Fitness': 'Gyms & Fitness',
        'map.cat.Transit': 'Transit',
        'map.cat.Shopping': 'Shopping',
        'map.cat.Other': 'Other',
        'map.loading': 'Loading map…',
        'map.error': 'Map couldn\u2019t load. Check your connection and try again.',
        'map.footnote': 'Map data © OpenStreetMap contributors. Pin locations are illustrative.',
        'map.projectLocation': 'Project location',
        'map.openInMaps': 'Open in Maps ›',
        'map.filterLabel': 'Filter points of interest by category',
        'map.mapLabel': 'Neighborhood map',
        'units.title': 'Matching units',
        'units.noPhotos': 'No photos yet',
        'units.empty': 'No matching units to display.',
        'units.book': 'Book a visit',
        'units.bed': 'bed',
        'units.beds': 'beds',
        'units.bath': 'bath',
        'units.baths': 'baths',
        'units.sqft': 'sq ft',
        'units.sqftBalcony': 'sq ft balcony',
        'units.den': '+ Den',
        'units.interior': 'interior',
        'units.floorN': 'Floor {n}',
        'units.maximize': 'Enlarge photos',
        'units.minimize': 'Back to all units',
        'units.prevUnit': 'Previous home',
        'units.nextUnit': 'Next home',
        'units.nextHome': 'Next home →',
        'units.viewPlan': 'View Plan',
        'units.hidePlan': 'Hide plan',
        'units.nextUnitChip': 'Next home →',
        'units.pagerOf': 'Home {n} of {count}',
        'units.goToUnit': 'View {name}',
        'units.quietFloor': 'Floor {n} — higher floor, quieter',
        'note.stretch': 'About {v}% above budget — kept for the full match',
        'note.underMin': '{d}/month under your budget — matches everything else',
        'note.bedsMore': 'One more bedroom than requested, within budget',
        'note.denSub': 'The den can serve as your home office',
        'note.bedOffice': 'One bedroom can serve as your home office',
        'chat.fullscreen': 'Full screen',
        'chat.exitFullscreen': 'Exit full screen',
        'units.orientation': 'orientation',
        'units.orientationBullet': 'Orientation: {v}',
        'units.noiseBullet': 'Road noise: {v}',
        'units.viewBullet': 'View: {v}',
        'units.sunBullet': 'Sun exposure: {v}',
        'units.privacyBullet': 'Visual privacy: {v}',
        'units.bed1Bullet': 'Main bedroom: {v}',
        'units.livingBullet': 'Living room: {v}',
        'units.kitchenBullet': 'Kitchen: {v}',
        'units.balconyDims': 'Balcony: {v}',
        'feat.island': 'Kitchen island',
        'feat.counterSpace': 'Oversized counter space',
        'feat.counters': 'Extra counter space',
        'feat.corner': 'Corner unit',
        'feat.den': 'Den',
        'feat.ensuite': 'Ensuite bathroom',
        'feat.walkIn': 'Entrance walk-in closet',
        'feat.washerDryer': 'Side-by-side washer-dryer',
        'feat.backyard': 'Backyard',
        'feat.sideYard': 'Side yard',
        'feat.sun': 'Abundant natural light',
        'match.full': 'Full match',
        'match.partial': 'Partial match',
        'miss.bed': '{v} bedroom',
        'miss.beds': '{v} bedrooms',
        'miss.sqftMin': 'At least {v} sq ft',
        'miss.sqftMax': 'No more than {v} sq ft',
        'miss.priceMax': 'No more than {v}/month',
        'miss.priceMin': 'At least {v}/month',
        'miss.without': 'No {v}',
        'units.photosOf': '{name} photos',
        'units.photoAlt': '{name} — photo {n} of {count}',
        'units.prevPhoto': 'Previous photo',
        'units.nextPhoto': 'Next photo',
        'fp.title': 'Floor Plans',
        'fp.empty': 'No floor plans to display.',
        'fp.imageFallback': 'Floor plan',
        'fp.from': 'From'
    }
};

function normalize(lang) {
    if (typeof lang !== 'string') {
        return null;
    }
    const lower = lang.toLowerCase().slice(0, 2);
    return SUPPORTED.includes(lower) ? lower : null;
}

/**
 * Read a ?lang= override from the URL — the real query string first
 * (site/?lang=fr, also site/?lang=fr#/units), then inside the hash
 * (site/#/units?lang=fr), since the app routes on the hash.
 */
function langFromUrl() {
    try {
        const search = normalize(new URLSearchParams(window.location.search).get('lang'));
        if (search) {
            return search;
        }
        const hash = window.location.hash || '';
        const q = hash.indexOf('?');
        if (q !== -1) {
            return normalize(new URLSearchParams(hash.slice(q)).get('lang'));
        }
    } catch (e) {
        // no-op
    }
    return null;
}

// Apply a ?lang= override ONCE, at module load. It seeds the stored
// preference and then hands control to the toggle — re-reading it on every
// getLang() call would pin the language and dead-lock the EN/FR buttons for
// as long as the param sits in the address bar.
try {
    const urlLang = langFromUrl();
    if (urlLang) {
        localStorage.setItem(LANG_KEY, urlLang);
        document.documentElement.lang = urlLang;
    }
} catch (e) {
    // no-op
}

/** Resolve the active language: stored preference (URL-seeded) > 'en'. */
export function getLang() {
    try {
        const stored = normalize(localStorage.getItem(LANG_KEY));
        if (stored) {
            return stored;
        }
    } catch (e) {
        // no-op
    }
    return 'en';
}

/** Persist a language preference and notify subscribers. */
export function setLang(lang) {
    const normalized = normalize(lang) || 'en';
    try {
        localStorage.setItem(LANG_KEY, normalized);
    } catch (e) {
        // no-op
    }
    // If the address bar carries a lang pin, rewrite it to the new choice so
    // a reload (or a copied link) matches what's on screen.
    try {
        const url = new URL(window.location.href);
        let changed = false;
        if (url.searchParams.has('lang') && normalize(url.searchParams.get('lang')) !== normalized) {
            url.searchParams.set('lang', normalized);
            changed = true;
        }
        const hash = url.hash || '';
        const q = hash.indexOf('?');
        if (q !== -1) {
            const hashParams = new URLSearchParams(hash.slice(q + 1));
            if (hashParams.has('lang') && normalize(hashParams.get('lang')) !== normalized) {
                hashParams.set('lang', normalized);
                url.hash = hash.slice(0, q + 1) + hashParams.toString();
                changed = true;
            }
        }
        if (changed) {
            window.history.replaceState(null, '', url.toString());
        }
    } catch (e) {
        // no-op
    }
    try {
        document.documentElement.lang = normalized;
        window.dispatchEvent(new CustomEvent('awac:lang-changed', { detail: { lang: normalized } }));
    } catch (e) {
        // no-op
    }
}

/** Translate a key, with optional {placeholder} substitutions. */
export function t(key, vars) {
    const lang = getLang();
    const table = STRINGS[lang] || STRINGS.fr;
    let value = table[key] != null ? table[key] : (STRINGS.en[key] != null ? STRINGS.en[key] : key);
    if (vars) {
        Object.keys(vars).forEach((name) => {
            value = value.split(`{${name}}`).join(String(vars[name]));
        });
    }
    return value;
}

/** React hook — returns [lang, setLang] and re-renders on language change. */
export function useAwacLang() {
    const [lang, setLangState] = useState(getLang);

    useEffect(() => {
        const onChange = (event) => {
            setLangState((event.detail && event.detail.lang) || getLang());
        };
        window.addEventListener('awac:lang-changed', onChange);
        return () => window.removeEventListener('awac:lang-changed', onChange);
    }, []);

    return [lang, setLang];
}
